import { useEffect, useState } from 'react'
import { SubmitHandler, useFieldArray, useForm } from 'react-hook-form'

import { Alert, Button, Grid } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import { ru } from 'date-fns/locale/ru'
import { CourseBlockState, Video, VideoSettingsState } from 'features/videos/service'
import { restoreVideoSettingsState } from 'features/videos/utils'
import { Course, CourseTypes } from 'services/courses'

import s from './VideoMainForm.module.css'

import { VideoHostingsSection } from './components/VideoHostingsSection'
import { VideoLocalesSection } from './components/VideoLocalesSection'
import { CodeController } from './controllers/code-controller/CodeController'
import { DurationController } from './controllers/duration-controller/DurationController'
import { ExcludedFromRecentController } from './controllers/excluded-from-recent-controller/ExcludedFromRecentController'
import { MentorController } from './controllers/mentor-controller/MentorController'
import { PublicVisibilityController } from './controllers/public-visibility-controller/PublicVisibilityController'
import { SlugController } from './controllers/slug-controller/SlugController'
import { StartDateController } from './controllers/start-date-controller/StartDateController'
import { VideoSettings } from './video-settings/VideoSettings'
import {
  createEmptyHostingForm,
  createEmptyLocaleForm,
  getInitialFormValues,
} from './videoMainForm.defaults'
import { buildVideoPayload } from './videoMainForm.submit'
import { VideoMainFormValues } from './videoMainForm.types'

type Props = {
  courses?: Course[]
  defaultValues?: Video
  isLoading: boolean
  submitHandler: SubmitHandler<Video>
}

const createEmptyBlock = (): CourseBlockState => ({
  id: crypto.randomUUID(),
  courseId: null,
  selectedPlaylistsIds: [],
  tabValue: false,
})

export const VideoMainForm = ({ courses, defaultValues, isLoading, submitHandler }: Props) => {
  const [courseBlocks, setCourseBlocks] = useState<CourseBlockState[]>([createEmptyBlock()])
  const [formMessage, setFormMessage] = useState('')
  const [videoSettingsState, setVideoSettingsState] = useState<VideoSettingsState>({})

  const { control, getValues, handleSubmit, reset, setError, setValue } =
    useForm<VideoMainFormValues>({
      defaultValues: getInitialFormValues(defaultValues),
      mode: 'all',
    })
  const {
    fields: localeFields,
    append: appendLocale,
    remove: removeLocale,
  } = useFieldArray({
    control,
    name: 'locales',
  })
  const {
    fields: hostingFields,
    append: appendHosting,
    remove: removeHosting,
  } = useFieldArray({
    control,
    name: 'hostings',
  })

  const checkSelectedPlaylistsRequiredSettings = () => {
    for (const playlistId in videoSettingsState) {
      if (videoSettingsState[playlistId].length === 0) {
        // Find which block contains this playlist and set its tabValue
        const block = courseBlocks.find(b => b.selectedPlaylistsIds.includes(playlistId))

        if (block) {
          setCourseBlocks(prev =>
            prev.map(b => (b.id === block.id ? { ...b, tabValue: playlistId } : b))
          )
        }

        return true
      }
    }
  }

  const isPublicCourseSelected = (): boolean => {
    return courseBlocks.some(block => {
      if (!block.courseId) {
        return false
      }
      const course = courses?.find(c => c.id === block.courseId)

      return course?.type === CourseTypes.Public
    })
  }

  const onSubmit = (formValues: VideoMainFormValues) => {
    setFormMessage('')

    if (checkSelectedPlaylistsRequiredSettings()) {
      return
    }

    if (isPublicCourseSelected() && !formValues.slug?.trim()) {
      setError('slug', { type: 'required' })

      return
    }

    const submitPayloadResult = buildVideoPayload(formValues)

    if (!submitPayloadResult.payload) {
      setFormMessage(submitPayloadResult.error || 'Не удалось подготовить данные формы.')

      return
    }

    const settings = Object.values(videoSettingsState).flat()
    const tags = defaultValues ? defaultValues.tags : []

    submitHandler({ ...submitPayloadResult.payload, settings, tags } as Video)
  }

  const submitButtonDisable = isLoading

  useEffect(() => {
    if (defaultValues) {
      // Group settings by courseId to create blocks
      const settingsByCourse = defaultValues.settings.reduce(
        (acc, setting) => {
          const key = setting.courseId

          if (!acc[key]) {
            acc[key] = []
          }
          acc[key].push(setting)

          return acc
        },
        {} as Record<number, typeof defaultValues.settings>
      )

      const blocks: CourseBlockState[] = Object.entries(settingsByCourse).map(
        ([courseId, courseSettings]) => {
          const playlistIds = Array.from(new Set(courseSettings.map(s => s.playlistId)))

          return {
            id: crypto.randomUUID(),
            courseId: Number(courseId),
            selectedPlaylistsIds: playlistIds,
            tabValue: playlistIds[0] || false,
          }
        }
      )

      if (blocks.length > 0) {
        setCourseBlocks(blocks)
      }

      const allPlaylistIds = Array.from(new Set(defaultValues.settings.map(s => s.playlistId)))

      const defaultVideoSettingsState = restoreVideoSettingsState(
        allPlaylistIds,
        defaultValues.settings
      )

      setVideoSettingsState(defaultVideoSettingsState)
    } else {
      setCourseBlocks([createEmptyBlock()])
      setVideoSettingsState({})
      setFormMessage('')
    }

    reset(getInitialFormValues(defaultValues))
  }, [defaultValues, reset])

  return (
    <form className={s.container} onSubmit={handleSubmit(onSubmit)}>
      <LocalizationProvider adapterLocale={ru} dateAdapter={AdapterDateFns}>
        <SlugController control={control} />
        <PublicVisibilityController control={control} />

        {formMessage && <Alert severity={'error'}>{formMessage}</Alert>}

        <VideoLocalesSection
          control={control}
          fields={localeFields}
          onAdd={() => appendLocale(createEmptyLocaleForm())}
          onRemove={removeLocale}
        />

        <VideoHostingsSection
          control={control}
          fields={hostingFields}
          onAdd={() => appendHosting(createEmptyHostingForm(getValues('locales.0.locale') || 'ru'))}
          onRemove={removeHosting}
        />

        <div className={s.flexContainer}>
          <MentorController control={control} setValue={setValue} />

          <StartDateController control={control} setValue={setValue} />

          <CodeController control={control} />

          <DurationController control={control} />
        </div>

        <ExcludedFromRecentController control={control} />

        <VideoSettings
          courseBlocks={courseBlocks}
          courses={courses}
          setCourseBlocks={setCourseBlocks}
          setVideoSettingsState={setVideoSettingsState}
          videoSettingsState={videoSettingsState}
        />

        <Grid container justifyContent={'flex-end'} marginTop={4}>
          <Button disabled={submitButtonDisable} type={'submit'}>
            Сохранить
          </Button>
        </Grid>
      </LocalizationProvider>
    </form>
  )
}
